import { inject, Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { expand, reduce } from 'rxjs/operators';

import { ApiService } from '../../../core/services/api';
import { api } from '../../../models/api-url.model';
import type {
  AssignToGroupResponse,
  ChangePasswordRequest,
  DeactivateOktaUserRequest,
  FederationCheckResponse,
  MakeFederatedResponse,
  OktaDashboardUser,
  OktaUserExistsResponse,
  OktaUserRequest,
  OktaUserResponse,
  OktaUsersListResponse,
} from './okta-user.types';

@Injectable({
  providedIn: 'root',
})
export class OktaUserService {
  private readonly apiService = inject(ApiService);

  getUsers(includeMfa = true): Observable<OktaDashboardUser[]> {
    const fetchPage = (after?: string): Observable<OktaUsersListResponse> => {
      const params = new URLSearchParams({ includeMfa: String(includeMfa) });
      if (after) params.set('after', after);
      return this.apiService.get<OktaUsersListResponse>(
        `${api.oktaUsersURL}?${params}`,
      );
    };

    return fetchPage().pipe(
      expand((res) =>
        res.data.nextCursor ? fetchPage(res.data.nextCursor) : EMPTY,
      ),
      reduce(
        (acc, res) => [...acc, ...res.data.users],
        [] as OktaDashboardUser[],
      ),
    );
  }

  checkFederation(username: string): Observable<FederationCheckResponse> {
    return this.apiService.get(
      `${api.oktaUsersURL}/${encodeURIComponent(username)}/is-federated`,
    );
  }

  checkExists(username: string): Observable<OktaUserExistsResponse> {
    return this.apiService.get(
      `${api.oktaUsersURL}/${encodeURIComponent(username)}/exists`,
    );
  }

  assignToGroup(username: string): Observable<AssignToGroupResponse> {
    return this.apiService.put(
      `${api.oktaUsersURL}/${encodeURIComponent(username)}/assign-group`,
    );
  }

  makeFederated(username: string): Observable<MakeFederatedResponse> {
    return this.apiService.put(
      `${api.oktaUsersURL}/${encodeURIComponent(username)}/make-federated`,
    );
  }

  createUser(payload: OktaUserRequest): Observable<OktaUserResponse> {
    return this.apiService.post(`${api.oktaUsersURL}`, payload);
  }

  updateUser(payload: OktaUserRequest): Observable<OktaUserResponse> {
    return this.apiService.put(`${api.oktaUsersURL}/${payload.login}`, payload);
  }

  deactivateUser(
    payload: DeactivateOktaUserRequest,
  ): Observable<OktaUserResponse> {
    return this.apiService.put(
      `${api.oktaUsersURL}/${payload.login}/deactivate`,
      payload,
    );
  }

  resendActivationEmail(login: string): Observable<OktaUserResponse> {
    return this.apiService.put(
      `${api.oktaUsersURL}/${encodeURIComponent(login)}/reactivate`,
    );
  }

  onboardUser(payload: OktaUserRequest): Observable<OktaUserResponse> {
    return this.apiService.post(`${api.onboardOktaUsersURL}`, payload);
  }

  onboardUserWithCustomActivationEmail(
    payload: OktaUserRequest,
  ): Observable<OktaUserResponse> {
    return this.apiService.post(
      `${api.onboardOktaUsersCustomActivationEmailURL}`,
      payload,
    );
  }

  changePassword(payload: ChangePasswordRequest): Observable<OktaUserResponse> {
    return this.apiService.post(`${api.changeOktaPasswordURL}`, payload);
  }

  forgotPassword(login: string): Observable<OktaUserResponse> {
    return this.apiService.post(`${api.forgotOktaPasswordURL}`, { login });
  }

  unlockUser(login: string): Observable<OktaUserResponse> {
    return this.apiService.put(
      `${api.oktaUsersURL}/${encodeURIComponent(login)}/unlock`,
    );
  }

  resetMfaFactor(
    login: string,
    factorId: string,
  ): Observable<OktaUserResponse> {
    return this.apiService.delete(
      `${api.oktaUsersURL}/${encodeURIComponent(login)}/factors/${encodeURIComponent(factorId)}`,
    );
  }

  deleteUser(username: string): Observable<OktaUserResponse> {
    return this.apiService.delete(
      `${api.oktaUsersURL}/${encodeURIComponent(username)}`,
    );
  }

  startOktaMigration(email: string): Observable<OktaUserResponse> {
    return this.apiService.post(`${api.oktaMigrationURL}`, { email });
  }
}
